import type { ChangeSummary } from "@models/project";
import AddIcon from "@mui/icons-material/AddRounded";
import EditIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMoreRounded";
import RemoveIcon from "@mui/icons-material/RemoveRounded";
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";

interface UploadDiffSummaryProps {
  diff: ChangeSummary | Record<string, never> | undefined;
  versionNumber?: number | null;
  /** User-supplied semver (or auto-incremented). Preferred over versionNumber for display when present. */
  semverLabel?: string | null;
  status?: "created" | "deduped";
}

/**
 * Compact summary of the ref + net-topology diff that comes back with a
 * new :SchematicVersion. Shown right after the user successfully uploads a
 * netlist so they can see what changed — expand to see the exact refs.
 */
export function UploadDiffSummary({
  diff,
  versionNumber,
  semverLabel,
  status = "created",
}: UploadDiffSummaryProps) {
  const [expanded, setExpanded] = useState(false);

  // Prefer the user's semver label everywhere we render the version
  // identifier — falls back to v{N} on legacy uploads.
  const versionDisplay =
    semverLabel?.trim() || (versionNumber != null ? `v${versionNumber}` : "");

  if (!diff || !("totals" in diff)) return null;
  const { totals, components, nets } = diff as ChangeSummary;

  const hasAny =
    totals.components_added +
      totals.components_removed +
      totals.components_modified +
      totals.nets_added +
      totals.nets_removed +
      totals.nets_topology_changed >
    0;

  if (status === "deduped") {
    return (
      <Alert severity="info">
        <AlertTitle>No changes detected</AlertTitle>
        The uploaded file is byte-identical to the current version. Nothing was
        saved.
      </Alert>
    );
  }

  if (!hasAny && versionNumber === 1) {
    return (
      <Alert severity="success">
        <AlertTitle>{versionDisplay} saved</AlertTitle>
        Initial version — {totals.components_added} components and{" "}
        {totals.nets_added} nets imported.
      </Alert>
    );
  }

  if (!hasAny) {
    return (
      <Alert severity="success">
        <AlertTitle>Saved as {versionDisplay}</AlertTitle>
        No detectable structural changes between this upload and the previous
        version.
      </Alert>
    );
  }

  return (
    <Alert
      severity="success"
      icon={false}
      sx={{ borderRadius: 2 }}
      action={
        <IconButton
          size="small"
          onClick={() => setExpanded((v) => !v)}
          sx={{
            transform: expanded ? "rotate(180deg)" : "none",
            transition: "transform 150ms",
          }}
          aria-label={expanded ? "Hide details" : "Show details"}
        >
          <ExpandMoreIcon />
        </IconButton>
      }
    >
      <AlertTitle>Saved as {versionDisplay}</AlertTitle>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
        {totals.components_added > 0 && (
          <Chip
            size="small"
            icon={<AddIcon />}
            label={`${totals.components_added} component${
              totals.components_added === 1 ? "" : "s"
            } added`}
            color="success"
            variant="outlined"
          />
        )}
        {totals.components_removed > 0 && (
          <Chip
            size="small"
            icon={<RemoveIcon />}
            label={`${totals.components_removed} removed`}
            color="error"
            variant="outlined"
          />
        )}
        {totals.components_modified > 0 && (
          <Chip
            size="small"
            icon={<EditIcon />}
            label={`${totals.components_modified} modified`}
            color="warning"
            variant="outlined"
          />
        )}
        {totals.nets_added > 0 && (
          <Chip
            size="small"
            icon={<AddIcon />}
            label={`${totals.nets_added} net${
              totals.nets_added === 1 ? "" : "s"
            } added`}
            color="success"
            variant="outlined"
          />
        )}
        {totals.nets_removed > 0 && (
          <Chip
            size="small"
            icon={<RemoveIcon />}
            label={`${totals.nets_removed} net${
              totals.nets_removed === 1 ? "" : "s"
            } removed`}
            color="error"
            variant="outlined"
          />
        )}
        {totals.nets_topology_changed > 0 && (
          <Chip
            size="small"
            icon={<EditIcon />}
            label={`${totals.nets_topology_changed} topology change${
              totals.nets_topology_changed === 1 ? "" : "s"
            }`}
            color="warning"
            variant="outlined"
          />
        )}
      </Stack>

      <Collapse in={expanded} sx={{ mt: 1 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {components?.added && components.added.length > 0 && (
            <RefList
              title="Components added"
              items={components.added}
              tone="success"
            />
          )}
          {components?.removed && components.removed.length > 0 && (
            <RefList
              title="Components removed"
              items={components.removed}
              tone="error"
            />
          )}
          {components?.modified && components.modified.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Modified ({components.modified.length})
              </Typography>
              <Stack
                direction="row"
                spacing={0.5}
                flexWrap="wrap"
                sx={{ mt: 0.5 }}
              >
                {components.modified.slice(0, 12).map((m) => (
                  <Chip
                    key={m.ref}
                    size="small"
                    label={m.ref}
                    variant="outlined"
                    title={Object.entries(m.changes)
                      .map(
                        ([k, v]) =>
                          `${k}: ${JSON.stringify(v.from)} → ${JSON.stringify(v.to)}`,
                      )
                      .join("\n")}
                  />
                ))}
                {components.modified.length > 12 && (
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", alignSelf: "center" }}
                  >
                    +{components.modified.length - 12} more
                  </Typography>
                )}
              </Stack>
            </Box>
          )}
          {nets?.added && nets.added.length > 0 && (
            <RefList title="Nets added" items={nets.added} tone="success" />
          )}
          {nets?.removed && nets.removed.length > 0 && (
            <RefList title="Nets removed" items={nets.removed} tone="error" />
          )}
          {nets?.topology_changed && nets.topology_changed.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Nets with topology changes ({nets.topology_changed.length})
              </Typography>
              <Stack
                direction="row"
                spacing={0.5}
                flexWrap="wrap"
                sx={{ mt: 0.5 }}
              >
                {nets.topology_changed.slice(0, 12).map((n) => (
                  <Chip
                    key={n.name}
                    size="small"
                    label={n.name}
                    variant="outlined"
                    title={[
                      ...n.pins_added.map((p) => `+ ${p}`),
                      ...n.pins_removed.map((p) => `- ${p}`),
                    ].join("\n")}
                  />
                ))}
                {nets.topology_changed.length > 12 && (
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", alignSelf: "center" }}
                  >
                    +{nets.topology_changed.length - 12} more
                  </Typography>
                )}
              </Stack>
            </Box>
          )}
        </Box>
      </Collapse>
    </Alert>
  );
}

function RefList({
  title,
  items,
  tone,
}: {
  title: string;
  items: string[];
  tone: "success" | "error";
}) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {title} ({items.length})
      </Typography>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
        {items.slice(0, 20).map((r) => (
          <Chip
            key={r}
            size="small"
            label={r}
            color={tone}
            variant="outlined"
            sx={{ fontFamily: "monospace" }}
          />
        ))}
        {items.length > 20 && (
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", alignSelf: "center" }}
          >
            +{items.length - 20} more
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
